/**
 * Layout component with navigation and user menu
 * v2.0.0 - Mobile-first redesign with bottom navigation
 */

import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { BotStatusIndicator } from './BotStatusIndicator';
import { 
  LayoutDashboard, ClipboardList, CreditCard, History, LogOut,
  BarChart3, DollarSign, Zap, Menu, X, ChevronRight, Package, ShoppingCart, AlertTriangle
} from 'lucide-react';
import silviaPng from '../assets/_silvia_white.png';
import '../styles/Layout.css';

const APP_VERSION = '2.0.0';

// Bottom nav items (mobile) — max 5 for thumb reach
const BOTTOM_NAV = [
  { path: '/stats', icon: BarChart3, label: 'Dashboard' },
  { path: '/wc-orders', icon: ShoppingCart, label: 'Zamówienia' },
  { path: '/finance', icon: DollarSign, label: 'Finanse' },
  { path: '/performance', icon: Zap, label: 'Wydajność' },
  { path: '/bot', icon: LayoutDashboard, label: 'Bot' },
];

// Sidebar nav items (desktop) — full list
const SIDEBAR_NAV = [
  { path: '/stats', icon: BarChart3, label: '📊 Dashboard' },
  { path: '/wc-orders', icon: ShoppingCart, label: '🛒 Zamówienia WC' },
  { path: '/order-analytics', icon: Package, label: '📦 Analityka zamówień' },
  { path: '/finance', icon: DollarSign, label: '💰 Finanse' },
  { path: '/complaints', icon: AlertTriangle, label: '⚠️ Reklamacje' },
  { path: '/performance', icon: Zap, label: '⚡ Wydajność' },
  { type: 'divider' as const, label: 'Bot Silvia' },
  { path: '/bot', icon: LayoutDashboard, label: 'Bot Dashboard' },
  { path: '/orders', icon: ClipboardList, label: 'Zamówienia (Bot)' },
  { path: '/payments', icon: CreditCard, label: 'Płatności (Bot)' },
  { path: '/history', icon: History, label: 'Historia (Bot)' },
];

export function Layout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="layout">
      {/* ── Desktop Sidebar ── */}
      <nav className="sidebar desktop-only">
        <div className="sidebar-bg">
          <img src={silviaPng} alt="" className="sidebar-bg-img" aria-hidden="true" />
          <div className="sidebar-bg-overlay"></div>
        </div>
        
        <div className="sidebar-content">
          <div className="sidebar-logo-container">
            <div className="sparkle sparkle-1"></div>
            <div className="sparkle sparkle-2"></div>
            <div className="sparkle sparkle-3"></div>
            <div className="sparkle sparkle-4"></div>
            <div className="sparkle sparkle-5"></div>
            <div className="light-flare light-flare-1"></div>
            <div className="light-flare light-flare-2"></div>
            <img src={silviaPng} alt="Silvia" className="sidebar-logo-img" />
          </div>
          
          <div className="sidebar-blur-divider"></div>
          
          <div className="sidebar-header">
            <div className="logo-icon"><span style={{ fontSize: 32 }}>✨</span></div>
            <h2>Silvia</h2>
            <p>Kamar Dashboard</p>
          </div>

          <div className="bot-status-container">
            <BotStatusIndicator />
          </div>

          <ul className="nav-links">
            {SIDEBAR_NAV.map((item, i) => {
              if ('type' in item && item.type === 'divider') {
                return <li key={i} className="nav-divider"><span>{item.label}</span></li>;
              }
              const Icon = item.icon!;
              return (
                <li key={item.path}>
                  <Link to={item.path!} className={isActive(item.path!) ? 'active' : ''}>
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="sidebar-footer">
            <div className="footer-top-row">
              <div className="silvia-badge">v{APP_VERSION}</div>
            </div>
            <div className="user-info">
              <div className="user-email">{user?.email}</div>
              <button onClick={signOut} className="logout-button">
                <LogOut size={16} />
                <span>Wyloguj się</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile Top Bar ── */}
      <header className="mobile-topbar mobile-only">
        <div className="mobile-topbar-left">
          <span className="mobile-logo">✨ Silvia</span>
        </div>
        <div className="mobile-topbar-right">
          <BotStatusIndicator compact />
          <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* ── Mobile Drawer Menu ── */}
      {menuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mobile-drawer" onClick={e => e.stopPropagation()}>
            <div className="mobile-drawer-header">
              <img src={silviaPng} alt="Silvia" className="mobile-drawer-logo" />
              <div>
                <h3>Silvia v{APP_VERSION}</h3>
                <p className="mobile-drawer-email">{user?.email}</p>
              </div>
            </div>
            <nav className="mobile-drawer-nav">
              {SIDEBAR_NAV.map((item, i) => {
                if ('type' in item && item.type === 'divider') {
                  return <div key={i} className="mobile-drawer-divider"><span>{item.label}</span></div>;
                }
                const Icon = item.icon!;
                return (
                  <Link 
                    key={item.path} 
                    to={item.path!} 
                    className={`mobile-drawer-link ${isActive(item.path!) ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                    <ChevronRight size={16} className="drawer-arrow" />
                  </Link>
                );
              })}
            </nav>
            <div className="mobile-drawer-footer">
              <button onClick={signOut} className="mobile-logout-btn">
                <LogOut size={16} />
                <span>Wyloguj się</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main Content ── */}
      <main className="main-content">
        <Outlet />
      </main>

      {/* ── Mobile Bottom Navigation ── */}
      <nav className="bottom-nav mobile-only">
        {BOTTOM_NAV.map(item => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link key={item.path} to={item.path} className={`bottom-nav-item ${active ? 'active' : ''}`}>
              <Icon size={20} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
